import {
  Coverages,
  PremiumBreakdown,
  SavingsBreakdown,
  SimulationWarning,
} from '../../../playground/endpoints/_shared';
import { simulateV2Product } from '../../api';
import { FamilySavingsV2FormValues } from './schema';

export const SLUG = 'family-savings-insurance';

/**
 * A `projection_by_year` row, as `output/yearly.py` groups it. The withdrawal
 * trio is absent unless the run actually scheduled a withdrawal, and
 * `withdrawable_amount` plus the pre/post pair are `debug`-only -- this page
 * always asks for debug, so they arrive whenever withdrawals do.
 */
export interface FamilySavingsV2YearlyRow {
  year: number;
  age: number;
  actuarial_age: number;
  accumulated_prime: number;
  policy_value: number;
  death_compensation: number;
  coverage_costs: number;
  policy_expenses: number;
  rentability: number;
  withdrawable_amount?: number;
  withdrawal?: number;
  policy_value_before_withdrawal?: number;
  policy_value_after_withdrawal?: number;
  [key: string]: number | undefined;
}

/**
 * A `projection_by_month` row, as `output/monthly.py` builds it. Note the
 * absence of `calendar_month`: unlike APV Select and Life Max, this product's
 * monthly rows carry no calendar column, so the month is reported as a bare
 * index.
 */
export interface FamilySavingsV2MonthlyRow {
  month: number;
  year: number;
  age: number;
  actuarial_age: number;
  policy_value: number;
  rentability: number;
  net_contribution: number;
  cost: number;
  agreed_premium: number;
  accumulated_prime: number;
  coverage_costs: number;
  coverage_costs_breakdown?: Record<string, number>;
  policy_expenses: number;
  death_compensation: number;
  withdrawable_amount: number;
  withdrawal?: number;
  policy_value_before_withdrawal?: number;
  policy_value_after_withdrawal?: number;
}

/**
 * `output/serializer.py` plus `output/extra_output.py`. The solver findings are
 * reported only by the modes that ran one: `coverage_factor` / `funded_goal`
 * come from savings_capacity's coverage solve, `terminal_excess` from that or
 * from withdrawal_goal.
 */
export interface FamilySavingsV2Response {
  uf_value: number;
  currency: string;
  timeframe_months: number;
  premium: PremiumBreakdown;
  coverages: Coverages;
  savings: SavingsBreakdown;
  projection_by_year: FamilySavingsV2YearlyRow[];
  projection_by_month?: FamilySavingsV2MonthlyRow[];
  warnings: SimulationWarning[];
  goal: number;
  withdrawal_amount?: number;
  withdrawal_period_months?: number;
  terminal_excess?: number;
  coverage_factor?: number;
  funded_goal?: number;
  objective_achieved_month?: number;
}

/**
 * The rate fields as `FamilySavingsInput` wants them, which is NOT what
 * `_shared`'s `buildRatePayload` produces:
 *
 * - `rate_mode` must travel. v2 defaults it to `fixed` and then demands the
 *   matching field (`product_validators.assert_rate_fields_present`), while
 *   `assert_only_one_rate_input_provided` rejects the other one being present.
 * - v2's own name for the stepped mode is `tiered`; `stepped` is the form's
 *   label for the same thing.
 * - The schedule travels as a real JSON array. `_shared` stringifies it
 *   because v1 rides on query params; this route posts a JSON body, and
 *   `RateScheduleFromFlatJson` accepts the array directly.
 */
function buildRatePayload(values: FamilySavingsV2FormValues) {
  return values.rate_mode === 'fixed'
    ? { rate_mode: 'fixed', projection_rate: values.projection_rate }
    : {
      rate_mode: 'tiered',
      projection_rate_schedule: values.projection_rate_schedule ?? [],
    };
}

export function buildFamilySavingsV2Payload(values: FamilySavingsV2FormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    // UI-only: drives objective-field visibility. The engine infers the
    // objective from the presence of `goal`. Present only on the
    // savings_capacity variant, so the type is widened to strip it uniformly.
    capacity_objective: capacityObjective,
    timeframe,
    ...rest
  } = values as FamilySavingsV2FormValues & { capacity_objective?: string };

  // An objective the tester turned off must not travel: a leftover `goal` from
  // a previous submit would put savings_capacity back into its coverage solve.
  const dropsObjective = values.savings_mode === 'savings_capacity'
    && capacityObjective !== 'goal';

  return {
    ...rest,
    ...(dropsObjective ? { goal: undefined, goal_horizon_month: undefined } : {}),
    ...buildRatePayload(values),
    // Omitted rather than sent as null: `apply_default_timeframe` only fills
    // the field in when it is absent.
    ...(timeframe === undefined ? {} : { timeframe }),
    // Asks the engine for `projection_by_month`, and keeps the debug-only
    // columns on the yearly rows that this page's tables read.
    debug: true,
  };
}

export async function simulateFamilySavingsV2(
  values: FamilySavingsV2FormValues,
): Promise<FamilySavingsV2Response> {
  return simulateV2Product<FamilySavingsV2Response>(
    SLUG,
    buildFamilySavingsV2Payload(values),
  );
}
